import {
  BotIcon,
  BotMessageSquareIcon,
  GitPullRequestIcon,
  HouseIcon,
  ListChecksIcon,
  NotebookPenIcon,
  PaletteIcon,
  PlugIcon,
  ReceiptTextIcon,
  ShapesIcon,
  SquareKanbanIcon,
  TicketIcon,
  UserRoundIcon,
  UsersIcon,
  type LucideIcon,
} from "lucide-react"
import type { ShortcutId } from "@/shortcuts/registry.ts"

/*
 * The web app's pages that are always somewhere to go, as the sidebar (or the account menu) lists
 * them, the command palette offers them under "Go to", and their G-chords reach them. One list, so
 * they never disagree about what exists or which letter goes where.
 */

export type Destination = {
  to: string
  title: string
  Icon: LucideIcon
  /** The G-chord that goes there. */
  chord: ShortcutId
  /** It stays current on the pages beneath it, as Projects does on a Board. */
  nested?: true
  /**
   * It is listed in the account menu rather than the sidebar: a page a Member sets up once rather
   * than works in.
   */
  accountMenu?: true
}

/** Every Member's pages. */
export const destinations: readonly Destination[] = [
  { to: "/", title: "Home", Icon: HouseIcon, chord: "go-home" },
  { to: "/my-tickets", title: "My tickets", Icon: ListChecksIcon, chord: "go-my-tickets" },
  // Their Personal Project, which `/my-project` makes the first time and opens the Board of.
  { to: "/my-project", title: "My Project", Icon: NotebookPenIcon, chord: "go-my-project" },
  {
    to: "/projects",
    title: "Projects",
    Icon: SquareKanbanIcon,
    chord: "go-projects",
    nested: true,
  },
  // Everyone at the company, and each one's profile; an Admin manages Members here too.
  { to: "/people", title: "People", Icon: UsersIcon, chord: "go-people", nested: true },
  // Their own Profile and photo, which only they edit here.
  {
    to: "/profile",
    title: "Your Profile",
    Icon: UserRoundIcon,
    chord: "go-profile",
    accountMenu: true,
  },
  {
    to: "/connections",
    title: "My Connectors",
    Icon: PlugIcon,
    chord: "go-my-connectors",
    accountMenu: true,
  },
  // The Beacon MCP server, how to add it to an agent, the Member's own connected agents and their
  // access tokens.
  {
    to: "/connect-an-agent",
    title: "Connect an agent",
    Icon: BotMessageSquareIcon,
    chord: "go-connect-an-agent",
    accountMenu: true,
  },
]

/**
 * The pages only an Admin can reach, which the sidebar lists in its Administration view rather than
 * among every Member's pages.
 */
export const adminDestinations: readonly Destination[] = [
  { to: "/groups", title: "Groups", Icon: ShapesIcon, chord: "go-groups" },
  { to: "/agent", title: "Agent", Icon: BotIcon, chord: "go-agent" },
  { to: "/spend", title: "Spend", Icon: ReceiptTextIcon, chord: "go-spend" },
  // Beacon's Jira connection and the projects it adopted. Its address still answers any other
  // Member, whom Beacon in Slack sends there, with the Jira projects they can reach.
  { to: "/trackers", title: "Jira", Icon: TicketIcon, chord: "go-jira" },
  // Beacon's GitHub App and the organisations and accounts it is installed on.
  { to: "/github", title: "GitHub", Icon: GitPullRequestIcon, chord: "go-github" },
]

/** The design system's kitchen sink, listed last in Administration, with no chord of its own. */
export const designSystem = { to: "/kitchen-sink", title: "Design system", Icon: PaletteIcon }

/**
 * Whether a page is one of Administration's, which an Admin sees with the sidebar in its
 * Administration view: an Admin page, or a page beneath one, or the kitchen sink.
 */
export function inAdministration(pathname: string) {
  return [...adminDestinations, designSystem].some(
    ({ to }) => pathname === to || pathname.startsWith(`${to}/`),
  )
}
